import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Shield, Plus, Trash2, Edit2, Save, X, Loader2, ChevronDown, ChevronRight, Store, Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RolesView({ embedded = false, onRolesChange }) {
  const { isOwner } = useAuth();
  const [roles, setRoles] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formPermissions, setFormPermissions] = useState([]); // Legacy
  const [granularPermissions, setGranularPermissions] = useState([]); // New: [{key, store_ids}]
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, menuRes, storesRes] = await Promise.all([
        axios.get(`${API}/access-roles`),
        axios.get(`${API}/access-roles/menu-items`),
        axios.get(`${API}/access-roles/stores`),
      ]);
      setRoles(rolesRes.data);
      setMenuItems(menuRes.data);
      setStores(storesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const notifyChange = () => {
    if (onRolesChange) onRolesChange();
  };

  const resetForm = () => {
    setFormName("");
    setFormDesc("");
    setFormPermissions([]);
    setGranularPermissions([]);
    setExpandedSections({});
    setCreating(false);
    setEditingId(null);
  };

  const startEditing = (role) => {
    setEditingId(role.id);
    setFormName(role.name);
    setFormDesc(role.description || "");
    setFormPermissions([...role.permissions]);
    
    // Load granular permissions or convert legacy
    if (role.granular_permissions?.length > 0) {
      setGranularPermissions([...role.granular_permissions]);
    } else {
      // Convert legacy to granular format
      setGranularPermissions(role.permissions.map(k => ({ key: k, store_ids: null })));
    }
    setCreating(false);
  };

  const startCreating = () => {
    resetForm();
    setCreating(true);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Check if a permission key is enabled
  const hasPermission = (key) => {
    return granularPermissions.some(p => p.key === key);
  };

  // Get store_ids for a permission (null = all stores)
  const getStoreIds = (key) => {
    const perm = granularPermissions.find(p => p.key === key);
    return perm?.store_ids;
  };

  // Toggle a permission on/off
  const togglePermission = (key) => {
    if (hasPermission(key)) {
      // Remove permission
      setGranularPermissions(prev => prev.filter(p => p.key !== key));
    } else {
      // Add permission with all stores access
      setGranularPermissions(prev => [...prev, { key, store_ids: null }]);
    }
  };

  // Toggle a specific store for a permission
  const toggleStoreForPermission = (key, storeId) => {
    setGranularPermissions(prev => {
      const existing = prev.find(p => p.key === key);
      if (!existing) {
        // Add new permission with just this store
        return [...prev, { key, store_ids: [storeId] }];
      }
      
      // If currently "all stores", switch to specific stores starting with this one
      if (existing.store_ids === null) {
        return prev.map(p => p.key === key ? { ...p, store_ids: [storeId] } : p);
      }
      
      // Toggle this store in the list
      const newStoreIds = existing.store_ids.includes(storeId)
        ? existing.store_ids.filter(id => id !== storeId)
        : [...existing.store_ids, storeId];
      
      // If no stores selected, remove the permission
      if (newStoreIds.length === 0) {
        return prev.filter(p => p.key !== key);
      }
      
      return prev.map(p => p.key === key ? { ...p, store_ids: newStoreIds } : p);
    });
  };

  // Toggle "all stores" for a permission
  const toggleAllStores = (key) => {
    setGranularPermissions(prev => {
      const existing = prev.find(p => p.key === key);
      if (!existing) {
        return [...prev, { key, store_ids: null }];
      }
      
      // Toggle between all stores and no stores
      if (existing.store_ids === null) {
        return prev.filter(p => p.key !== key);
      } else {
        return prev.map(p => p.key === key ? { ...p, store_ids: null } : p);
      }
    });
  };

  // Check if a store is selected for a permission
  const isStoreSelected = (key, storeId) => {
    const perm = granularPermissions.find(p => p.key === key);
    if (!perm) return false;
    if (perm.store_ids === null) return true; // All stores
    return perm.store_ids.includes(storeId);
  };

  // Check if "all stores" is selected
  const isAllStoresSelected = (key) => {
    const perm = granularPermissions.find(p => p.key === key);
    return perm?.store_ids === null;
  };

  // Select all top-level permissions
  const selectAll = () => {
    const allPerms = [];
    menuItems.forEach(item => {
      allPerms.push({ key: item.key, store_ids: null });
      if (item.subsections) {
        item.subsections.forEach(sub => {
          allPerms.push({ key: sub.key, store_ids: null });
        });
      }
    });
    setGranularPermissions(allPerms);
  };

  const selectNone = () => setGranularPermissions([]);

  const saveRole = async () => {
    if (!formName.trim()) { toast.error("Role name is required"); return; }
    if (granularPermissions.length === 0) { toast.error("Select at least one permission"); return; }
    
    // Build legacy permissions from granular (top-level keys only)
    const legacyPerms = [...new Set(granularPermissions.map(p => p.key.split('.')[0]))];
    
    setSaving(true);
    try {
      const payload = { 
        name: formName, 
        description: formDesc, 
        permissions: legacyPerms,
        granular_permissions: granularPermissions
      };
      if (editingId) {
        await axios.put(`${API}/access-roles/${editingId}`, payload);
        toast.success("Role updated");
      } else {
        await axios.post(`${API}/access-roles`, payload);
        toast.success("Role created");
      }
      resetForm();
      fetchData();
      notifyChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save role"); }
    finally { setSaving(false); }
  };

  const deleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete role "${roleName}"? Users with this role will lose their assigned permissions.`)) return;
    try {
      await axios.delete(`${API}/access-roles/${roleId}`);
      toast.success("Role deleted");
      fetchData();
      notifyChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  // Helper to render permission description for a role card
  const renderPermissionSummary = (role) => {
    const perms = role.granular_permissions?.length > 0 ? role.granular_permissions : 
                  role.permissions.map(k => ({ key: k, store_ids: null }));
    
    return perms.slice(0, 6).map(perm => {
      const key = perm.key;
      const parts = key.split('.');
      const parent = menuItems.find(m => m.key === parts[0]);
      let label = key;
      
      if (parts.length === 1) {
        label = parent?.label || key;
      } else {
        const sub = parent?.subsections?.find(s => s.key === key);
        label = sub?.label || parts[1];
      }
      
      // Add store scope indicator
      let scopeLabel = '';
      if (perm.store_ids !== null && perm.store_ids.length > 0) {
        if (perm.store_ids.length === 1) {
          const store = stores.find(s => s.id === perm.store_ids[0]);
          scopeLabel = ` (${store?.name || '1 store'})`;
        } else {
          scopeLabel = ` (${perm.store_ids.length} stores)`;
        }
      }
      
      return (
        <span key={key} className="px-2 py-1 bg-stone-100 rounded text-xs text-stone-600">
          {label}{scopeLabel}
        </span>
      );
    });
  };

  if (loading) return (
    <div className={embedded ? "py-8 flex justify-center" : "min-h-screen flex items-center justify-center"}>
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  if (!isOwner) return (
    <div className={embedded ? "py-4" : "min-h-screen px-6 md:px-12 lg:px-16 py-10"} data-testid="roles-page">
      {!embedded && (
        <>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Access Control</span>
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Roles
          </h1>
        </>
      )}
      <p className="text-stone-500">Only the owner can manage access roles.</p>
    </div>
  );

  const isEditing = creating || editingId;

  return (
    <div className={embedded ? "" : "min-h-screen px-6 md:px-12 lg:px-16 py-10"} data-testid="roles-page">
      {/* Header - only show when not embedded */}
      {!embedded && (
        <>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Access Control</span>
          </div>
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 data-testid="roles-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Roles
              </h1>
              <p className="text-base text-stone-500">Create roles with specific menu access and assign them to team members</p>
            </div>
            {!isEditing && (
              <Button data-testid="create-role-btn" onClick={startCreating} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                <Plus className="w-4 h-4 mr-2" /> Create Role
              </Button>
            )}
          </div>
        </>
      )}

      {/* Embedded header */}
      {embedded && !isEditing && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-stone-500">Create roles with granular access to pages, sections, and specific stores</p>
          <Button data-testid="create-role-btn" onClick={startCreating} className="bg-stone-900 text-stone-50 hover:bg-stone-800" size="sm">
            <Plus className="w-4 h-4 mr-2" /> Create Role
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 max-w-6xl">
        {/* Role Form */}
        {isEditing && (
          <Card className="bg-white border border-stone-200/60 rounded-xl lg:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-stone-900">
                  {editingId ? "Edit Role" : "Create New Role"}
                </h2>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Role Name</label>
                    <Input 
                      data-testid="role-name-input"
                      value={formName} 
                      onChange={e => setFormName(e.target.value)}
                      placeholder="e.g., Store Manager, Interior Designer"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Description (optional)</label>
                    <Input 
                      data-testid="role-desc-input"
                      value={formDesc} 
                      onChange={e => setFormDesc(e.target.value)}
                      placeholder="Brief description of this role"
                    />
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-stone-700">Permissions</label>
                    <div className="flex gap-2">
                      <button onClick={selectAll} className="text-xs text-stone-500 hover:text-stone-700">Select All</button>
                      <span className="text-stone-300">|</span>
                      <button onClick={selectNone} className="text-xs text-stone-500 hover:text-stone-700">Clear</button>
                    </div>
                  </div>
                  
                  <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-[500px] overflow-y-auto">
                    {menuItems.map(item => {
                      const hasSubsections = item.subsections?.length > 0;
                      const isExpanded = expandedSections[item.key];
                      const parentEnabled = hasPermission(item.key);
                      
                      return (
                        <div key={item.key} className="bg-white">
                          {/* Parent item */}
                          <div 
                            className={`flex items-center gap-3 px-4 py-3 ${hasSubsections ? 'cursor-pointer hover:bg-stone-50' : ''}`}
                            onClick={() => hasSubsections && toggleSection(item.key)}
                          >
                            {hasSubsections ? (
                              isExpanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />
                            ) : (
                              <div className="w-4" />
                            )}
                            
                            <Checkbox 
                              data-testid={`perm-${item.key}`}
                              checked={parentEnabled}
                              onCheckedChange={() => togglePermission(item.key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm font-medium text-stone-700 flex-1">{item.label}</span>
                            
                            {parentEnabled && (
                              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                                {isAllStoresSelected(item.key) ? 'All stores' : `${getStoreIds(item.key)?.length || 0} stores`}
                              </span>
                            )}
                          </div>
                          
                          {/* Subsections */}
                          {hasSubsections && isExpanded && (
                            <div className="bg-stone-50/50 border-t border-stone-100">
                              {item.subsections.map(sub => {
                                const subEnabled = hasPermission(sub.key);
                                return (
                                  <div key={sub.key} className="pl-12 pr-4 py-2 border-b border-stone-100 last:border-b-0">
                                    <div className="flex items-center gap-3">
                                      <Checkbox 
                                        data-testid={`perm-${sub.key}`}
                                        checked={subEnabled}
                                        onCheckedChange={() => togglePermission(sub.key)}
                                      />
                                      <span className="text-sm text-stone-600 flex-1">{sub.label}</span>
                                    </div>
                                    
                                    {/* Store scoping for this subsection */}
                                    {subEnabled && stores.length > 0 && (
                                      <div className="mt-2 ml-7 flex flex-wrap gap-2">
                                        <button
                                          onClick={() => toggleAllStores(sub.key)}
                                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                            isAllStoresSelected(sub.key)
                                              ? 'bg-stone-900 text-white'
                                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                          }`}
                                        >
                                          <Globe className="w-3 h-3" /> All Stores
                                        </button>
                                        {stores.map(store => (
                                          <button
                                            key={store.id}
                                            onClick={() => toggleStoreForPermission(sub.key, store.id)}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                              isStoreSelected(sub.key, store.id) && !isAllStoresSelected(sub.key)
                                                ? 'bg-amber-100 text-amber-800'
                                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                            }`}
                                          >
                                            <Store className="w-3 h-3" /> {store.name}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Store scoping for parent (if no subsections) */}
                          {!hasSubsections && parentEnabled && stores.length > 0 && (
                            <div className="px-4 pb-3 flex flex-wrap gap-2 ml-7">
                              <button
                                onClick={() => toggleAllStores(item.key)}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                  isAllStoresSelected(item.key)
                                    ? 'bg-stone-900 text-white'
                                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                              >
                                <Globe className="w-3 h-3" /> All Stores
                              </button>
                              {stores.map(store => (
                                <button
                                  key={store.id}
                                  onClick={() => toggleStoreForPermission(item.key, store.id)}
                                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                    isStoreSelected(item.key, store.id) && !isAllStoresSelected(item.key)
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                  }`}
                                >
                                  <Store className="w-3 h-3" /> {store.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-stone-100">
                <Button 
                  data-testid="save-role-btn"
                  onClick={saveRole} 
                  disabled={saving}
                  className="bg-stone-900 text-stone-50 hover:bg-stone-800"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? "Update Role" : "Create Role"}
                </Button>
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Roles */}
        {roles.length === 0 && !isEditing ? (
          <Card className="bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl lg:col-span-2">
            <CardContent className="py-16 text-center">
              <Shield className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500 mb-4">No roles created yet</p>
              <Button onClick={startCreating} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" /> Create your first role
              </Button>
            </CardContent>
          </Card>
        ) : (
          roles.map(role => (
            <Card 
              key={role.id} 
              data-testid={`role-card-${role.id}`}
              className={`bg-white border rounded-xl transition-all ${
                editingId === role.id ? "border-stone-400 ring-2 ring-stone-200" : "border-stone-200/60"
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-stone-900">{role.name}</h3>
                    {role.description && (
                      <p className="text-sm text-stone-500 mt-0.5">{role.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => startEditing(role)}
                      className="text-stone-500 hover:text-stone-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => deleteRole(role.id, role.name)}
                      className="text-stone-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="text-xs text-stone-400 mb-2">Can access:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {renderPermissionSummary(role)}
                    {(role.granular_permissions?.length || role.permissions.length) > 6 && (
                      <span className="px-2 py-1 bg-stone-50 rounded text-xs text-stone-400">
                        +{(role.granular_permissions?.length || role.permissions.length) - 6} more
                      </span>
                    )}
                  </div>
                </div>

                <div className="pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-400">
                  <span>{role.granular_permissions?.length || role.permissions.length} permissions</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
